import React, { useEffect, useRef, useState } from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Tooltip,
  Button,
  Popover,
  MenuItem,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import {
  NavigateBefore as PreviousIcon,
  NavigateNext as NextIcon,
  Download,
  Print,
  ExpandMore,
  Warning as WarningIcon,
  AdminPanelSettings as AdminIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomResetIcon
} from '@mui/icons-material';
import AnnotationToolbar from './AnnotationToolbar';
import TextInputOverlay from './TextInputOverlay';
import AnnotationRenderer from './AnnotationRenderer';
import { 
  AnnotationType, 
  AnnotationStyle, 
  DEFAULT_ANNOTATION_STYLE,
  ShapeType,
  TextAnnotation,
  Annotation,
  AnnotationActionHistory,
  HighlightAnnotation,
  ShapeAnnotation,
  FreehandAnnotation
} from './types/AnnotationTypes';
import { useAuth } from '../../contexts/AuthContext';
import { DocumentPreviewInfo } from '../../services/api';

// Usa el worker local
GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

const EXPORT_ICONS_HEIGHT = 48; // px (ajusta si tus íconos son más grandes o pequeños)

interface SmartDocumentViewerProps {
  binaryData: ArrayBuffer | null;
  type: string;
  onDownloadWithAnnotations?: () => void;
  onDownloadWithoutAnnotations?: () => void;
  onPrintWithAnnotations?: () => void;
  onPrintWithoutAnnotations?: () => void;
  onSaveAnnotations?: () => void;
  annotations?: Annotation[];
  onAnnotationsChange?: (annotations: Annotation[]) => void;
  previewInfo?: DocumentPreviewInfo | null;
  isAdmin?: boolean;
}

const SmartDocumentViewer: React.FC<SmartDocumentViewerProps> = ({ binaryData, type, onDownloadWithAnnotations, onDownloadWithoutAnnotations, onPrintWithAnnotations, onPrintWithoutAnnotations, onSaveAnnotations, annotations: externalAnnotations, onAnnotationsChange, previewInfo, isAdmin }) => {
  const { user } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const renderTaskRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Helper function to generate unique IDs
  const generateUniqueId = (type: string) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `${type}-${timestamp}-${random}`;
  };
  
  // Estado para anotaciones
  const [currentTool, setCurrentTool] = useState<AnnotationType | null>(null);
  const [currentStyle, setCurrentStyle] = useState<AnnotationStyle>(DEFAULT_ANNOTATION_STYLE);
  const [selectedShape, setSelectedShape] = useState<ShapeType>('rectangle');
  const [canUndo, setCanUndo] = useState(false);
  const [annotations, setAnnotations] = useState<Annotation[]>(externalAnnotations || []);
  const [annotationHistory, setAnnotationHistory] = useState<Annotation[]>([]);
  const [isAddingText, setIsAddingText] = useState(false);
  const [textInputPosition, setTextInputPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  
  // Estado para drag profesional
  const [draggedAnnotationId, setDraggedAnnotationId] = useState<string | null>(null);
  const [dragStartPos, setDragStartPos] = useState<{ x: number; y: number } | null>(null);
  const [dragEndPos, setDragEndPos] = useState<{ x: number; y: number } | null>(null);
  
  // Estado para navegación de páginas
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pdfDocument, setPdfDocument] = useState<any>(null);

  // Estado para edición de anotaciones
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);
  const [editingText, setEditingText] = useState<string>('');

  // Definición del tipo de acción para el historial
  // Elimino cualquier definición local de AnnotationActionHistory en este archivo

  const [actionHistory, setActionHistory] = useState<AnnotationActionHistory[]>([]);

  // Estado para creación de highlight
  const [isDrawingHighlight, setIsDrawingHighlight] = useState(false);
  const [highlightStart, setHighlightStart] = useState<{ x: number; y: number } | null>(null);
  const [highlightRect, setHighlightRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [highlightNote, setHighlightNote] = useState<string>('');

  // Estado para creación de shapes
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [shapeStart, setShapeStart] = useState<{ x: number; y: number } | null>(null);
  const [shapeRect, setShapeRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Estado para freehand
  const [isDrawingFreehand, setIsDrawingFreehand] = useState(false);
  const [freehandPoints, setFreehandPoints] = useState<Array<{ x: number; y: number }>>([]);

  // Estado para exportación
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  // Estado para modal de progreso de impresión
  const [showPrintProgress, setShowPrintProgress] = useState(false);
  const [printProgress, setPrintProgress] = useState(0);
  const [printCurrentPage, setPrintCurrentPage] = useState(0);
  const [printTotalPages, setPrintTotalPages] = useState(0);
  const [printStatus, setPrintStatus] = useState('');

  // Estado para modal de progreso de descarga
  const [showDownloadProgress, setShowDownloadProgress] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadCurrentPage, setDownloadCurrentPage] = useState(0);
  const [downloadTotalPages, setDownloadTotalPages] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');

  // Estado para controlar visibilidad de anotaciones
  const [annotationsVisible, setAnnotationsVisible] = useState(false);

  // Estado para zoom
  const [zoom, setZoom] = useState(0.5); // Zoom inicial
  const [minZoom, setMinZoom] = useState(0.5); // Zoom mínimo
  const [maxZoom, setMaxZoom] = useState(3.0); // Zoom máximo
  const [zoomStep, setZoomStep] = useState(0.25); // Paso del zoom

  // Sync external annotations with better logic to prevent infinite loops
  useEffect(() => {
    console.log('🔄 Sync external annotations effect triggered');
    console.log('📊 externalAnnotations:', externalAnnotations);
    console.log('📊 current annotations:', annotations);
    
    if (externalAnnotations) {
      console.log('📊 External annotations count:', externalAnnotations.length);
      console.log('📊 External annotations IDs:', externalAnnotations.map(a => a.id));
      console.log('📊 Current annotations count:', annotations.length);
      console.log('📊 Current annotations IDs:', annotations.map(a => a.id));
      
      // Only update if the annotations are actually different
      const externalIds = externalAnnotations.map(a => a.id).sort().join(',');
      const currentIds = annotations.map(a => a.id).sort().join(',');
      
      if (externalIds !== currentIds) {
        console.log('🔄 Annotations are different, updating...');
        setAnnotations(externalAnnotations);
        console.log('✅ External annotations synced');
      } else {
        console.log('✅ Annotations are already in sync, skipping update');
      }
    }
  }, [externalAnnotations]);

  // Helper function to update annotations and notify parent
  const updateAnnotations = (updater: (prev: Annotation[]) => Annotation[]) => {
    console.log('🔄 updateAnnotations called');
    setAnnotations(prev => {
      console.log('🔄 updateAnnotations - prev annotations count:', prev.length);
      console.log('🔄 updateAnnotations - prev annotations IDs:', prev.map(a => a.id));
      
      const newAnnotations = updater(prev);
      console.log('🔄 updateAnnotations - new annotations count:', newAnnotations.length);
      console.log('🔄 updateAnnotations - new annotations IDs:', newAnnotations.map(a => a.id));
      
      // Only notify parent if annotations actually changed
      const prevIds = prev.map(a => a.id).sort().join(',');
      const newIds = newAnnotations.map(a => a.id).sort().join(',');
      
      if (prevIds !== newIds || prev.length !== newAnnotations.length) {
        if (onAnnotationsChange) {
          console.log('📤 Notifying parent with annotations:', newAnnotations);
          onAnnotationsChange(newAnnotations);
        } else {
          console.log('⚠️ onAnnotationsChange not provided');
        }
      } else {
        console.log('✅ No change detected, skipping parent notification');
      }
      
      return newAnnotations;
    });
  };

  useEffect(() => {
    if (!binaryData) return;

    // Cancel any previous render task
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    if (type.includes('pdf')) {
      // Render PDF with PDF.js
      // Create a copy of the ArrayBuffer to avoid DataCloneError
      const binaryDataCopy = binaryData.slice(0);
      const loadingTask = getDocument({ data: binaryDataCopy });
      
      loadingTask.promise.then((pdf: any) => {
        console.log('📄 PDF loaded with', pdf.numPages, 'pages');
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
        return pdf.getPage(currentPage);
      }).then((page: any) => {
        const viewport = page.getViewport({ scale: zoom });
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };
        
        // Store the render task so we can cancel it if needed
        renderTaskRef.current = page.render(renderContext);
        return renderTaskRef.current.promise;
      }).catch((error: any) => {
        if (error.name === 'AbortError') {
          console.log('PDF rendering was cancelled');
        } else {
          console.error('Error rendering PDF:', error);
        }
      });
    } else if (type.includes('jpeg') || type.includes('jpg') || type.includes('png')) {
      // Render image
      const blob = new Blob([binaryData], { type });
      const url = URL.createObjectURL(blob);
      if (imgRef.current) {
        imgRef.current.src = url;
      }
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    // For TIFF or unsupported types, do nothing (show message)
  }, [binaryData, type, zoom]);

  // Effect to render current page when it changes
  useEffect(() => {
    if (!pdfDocument || !binaryData || !type.includes('pdf')) return;

    // Cancel any previous render task
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    pdfDocument.getPage(currentPage).then((page: any) => {
      const viewport = page.getViewport({ scale: zoom });
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };
      
      // Store the render task so we can cancel it if needed
      renderTaskRef.current = page.render(renderContext);
      return renderTaskRef.current.promise;
    }).catch((error: any) => {
      if (error.name === 'AbortError') {
        console.log('PDF rendering was cancelled');
      } else {
        console.error('Error rendering PDF page:', error);
      }
    });
  }, [currentPage, pdfDocument, binaryData, type, zoom]);

  // Cleanup function to cancel any ongoing render task
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, []);

  // Actualiza canUndo cada vez que cambia el historial de acciones
  useEffect(() => {
    setCanUndo(actionHistory.length > 0);
  }, [actionHistory]);

  // Toolbar handlers
  const handleToolChange = (tool: AnnotationType | null) => {
    setCurrentTool(tool);
    console.log('🛠️ Tool selected:', tool);
  };

  const handleStyleChange = (style: AnnotationStyle) => {
    setCurrentStyle(style);
    console.log('🎨 Style updated:', style);
  };

  // Undo profesional: ahora también soporta edit y delete
  const handleUndo = () => {
    if (actionHistory.length === 0) return;
    const lastAction = actionHistory[actionHistory.length - 1];
    console.log('Deshaciendo acción:', lastAction);
    setActionHistory(hist => {
      const newHist = hist.slice(0, -1);
      console.log('Historial tras undo:', newHist);
      return newHist;
    });
    if (lastAction.type === 'add') {
      updateAnnotations(prev => prev.filter(a => a.id !== lastAction.annotation.id));
    } else if (lastAction.type === 'move') {
      updateAnnotations(prev => prev.map(a => {
        if (a.id === lastAction.id) {
          if (a.type === 'freehand') {
            // Para freehand, necesitamos mover todos los puntos del trazo
            const deltaX = lastAction.from.x - a.x;
            const deltaY = lastAction.from.y - a.y;
            return {
              ...a,
              x: lastAction.from.x,
              y: lastAction.from.y,
              points: (a as any).points.map((p: any) => ({
                x: p.x + deltaX,
                y: p.y + deltaY
              }))
            };
          } else {
            // Para otras anotaciones, solo restaurar la posición base
            return { ...a, x: lastAction.from.x, y: lastAction.from.y };
          }
        }
        return a;
      }));
    } else if (lastAction.type === 'edit') {
      updateAnnotations(prev => prev.map(a => {
        if (a.id === lastAction.id) {
          // Determinar si es una anotación de texto o de nota (highlight/shapes/freehand)
          if (a.type === 'highlight' || a.type === 'shapes' || a.type === 'freehand') {
            return { ...a, note: lastAction.from };
          } else {
            return { ...a, text: lastAction.from };
          }
        }
        return a;
      }));
    } else if (lastAction.type === 'delete') {
      updateAnnotations(prev => [...prev, lastAction.annotation]);
    }
  };

  const handleSave = () => {
    console.log('💾 Saving annotations...');
    console.log('📋 Current tool:', currentTool);
    console.log('🎨 Current style:', currentStyle);
    console.log('🔷 Selected shape:', selectedShape);
    console.log('📝 All annotations:', annotations);
    
    if (onSaveAnnotations) {
      onSaveAnnotations();
    }
  };

  const toggleAnnotationsVisibility = () => {
    setAnnotationsVisible(!annotationsVisible);
    // Si ocultamos las anotaciones, también desactivamos la herramienta actual
    if (annotationsVisible) {
      setCurrentTool(null);
    }
  };

  const handleShapeChange = (shape: ShapeType) => {
    setSelectedShape(shape);
    console.log('🔷 Shape selected:', shape);
  };

  // Funciones para manejar zoom
  const handleZoomIn = () => {
    setZoom(prevZoom => Math.min(prevZoom + zoomStep, maxZoom));
  };

  const handleZoomOut = () => {
    setZoom(prevZoom => Math.max(prevZoom - zoomStep, minZoom));
  };

  const handleZoomReset = () => {
    setZoom(1.5); // Reset al zoom inicial
  };

  const handleZoomChange = (newZoom: number) => {
    setZoom(Math.max(minZoom, Math.min(newZoom, maxZoom)));
  };

  const handleWheelZoom = (event: React.WheelEvent) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -zoomStep : zoomStep;
      setZoom(prevZoom => Math.max(minZoom, Math.min(prevZoom + delta, maxZoom)));
    }
  };

  // Handle canvas click for annotations
  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!currentTool) return; // Move tool is active

    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top + EXPORT_ICONS_HEIGHT;

    console.log('🖱️ Canvas clicked at:', { x, y });
    console.log('🛠️ Current tool:', currentTool);

    if (currentTool === 'text') {
      handleTextAnnotationClick(x, y);
    }
    // TODO: Add other annotation types
  };

  // Handle container click for annotations (when clicking outside canvas)
  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!currentTool || currentTool !== 'text') return;
    
    // Only handle if clicking on the container, not on canvas or other elements
    if (event.target === event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top + EXPORT_ICONS_HEIGHT;
      
      console.log('🖱️ Container clicked at:', { x, y });
      handleTextAnnotationClick(x, y);
    }
  };

  // Handle text annotation creation
  const handleTextAnnotationClick = (x: number, y: number) => {
    console.log('📝 Creating text annotation at:', { x, y });
    
    // Convert mouse coordinates to document coordinates considering zoom
    const documentX = x / zoom;
    const documentY = y / zoom;
    
    console.log('📝 Document coordinates:', { documentX, documentY, zoom });
    
    setTextInputPosition({ x: documentX, y: documentY });
    setIsAddingText(true);
  };

  // Handle text input submission
  const handleTextSubmit = (text: string) => {
    if (!textInputPosition || !text.trim()) {
      setIsAddingText(false);
      setTextInputPosition(null);
      return;
    }

    const newTextAnnotation: TextAnnotation = {
      id: generateUniqueId('text'),
      type: 'text',
      x: textInputPosition.x,
      y: textInputPosition.y,
      page: currentPage,
      style: currentStyle,
      createdAt: new Date(),
      createdBy: user?.username || 'Unknown',
      text: text.trim(),
    };

    // Sincronizar anotaciones e historial juntos
    updateAnnotations(prev => {
      const newAnnotations = [...prev, newTextAnnotation];
      setActionHistory(hist => [...hist, { type: 'add', annotation: newTextAnnotation }]);
      setCanUndo(true);
      return newAnnotations;
    });
    console.log('✅ Text annotation created:', newTextAnnotation);
    setIsAddingText(false);
    setTextInputPosition(null);
  };

  // Handle text input cancellation
  const handleTextCancel = () => {
    setIsAddingText(false);
    setTextInputPosition(null);
  };

  // Handler para iniciar edición (soporta texto, highlight, shapes y freehand)
  const handleEditAnnotation = (annotation: Annotation) => {
    console.log('✏️ handleEditAnnotation called for annotation:', annotation);
    console.log('🆔 Annotation ID:', annotation.id);
    console.log('📝 Annotation type:', annotation.type);
    console.log('📊 Current annotations count:', annotations.length);
    console.log('📊 All annotation IDs:', annotations.map(a => a.id));
    
    setEditingAnnotation(annotation);
    if (annotation.type === 'highlight' || annotation.type === 'shapes' || annotation.type === 'freehand') {
      const note = (annotation as any).note || '';
      console.log('📝 Setting editing text (note):', note);
      setEditingText(note);
    } else {
      const text = (annotation as any).text || '';
      console.log('📝 Setting editing text (text):', text);
      setEditingText(text);
    }
    setIsAddingText(false);
    setTextInputPosition(null);
    console.log('✅ Edit annotation setup completed');
  };

  // Handler para guardar edición (soporta texto, highlight, shapes y freehand)
  const handleEditSubmit = (text: string) => {
    console.log('✏️ handleEditSubmit called with text:', text);
    console.log('✏️ editingAnnotation:', editingAnnotation);
    
    if (!editingAnnotation) {
      console.log('❌ No editing annotation found');
      return;
    }
    
    console.log('🆔 Editing annotation ID:', editingAnnotation.id);
    console.log('📊 Current annotations count:', annotations.length);
    console.log('📊 All annotation IDs:', annotations.map(a => a.id));
    
    if (editingAnnotation.type === 'highlight' || editingAnnotation.type === 'shapes' || editingAnnotation.type === 'freehand') {
      const prevNote = (editingAnnotation as any).note;
      console.log('📝 Updating note from:', prevNote, 'to:', text);
      
      updateAnnotations(prev => {
        console.log('🔄 updateAnnotations callback - prev count:', prev.length);
        console.log('🔄 updateAnnotations callback - prev IDs:', prev.map(a => a.id));
        
        const updated = prev.map(a => {
          if (a.id === editingAnnotation.id) {
            console.log('✅ Found annotation to update:', a.id);
            return { ...a, note: text };
          }
          return a;
        });
        
        console.log('🔄 updateAnnotations callback - updated count:', updated.length);
        console.log('🔄 updateAnnotations callback - updated IDs:', updated.map(a => a.id));
        
        return updated;
      });
      setActionHistory(hist => [...hist, { type: 'edit', id: editingAnnotation.id, from: prevNote, to: text }]);
    } else {
      const prevText = (editingAnnotation as any).text;
      console.log('📝 Updating text from:', prevText, 'to:', text);
      
      updateAnnotations(prev => {
        console.log('🔄 updateAnnotations callback - prev count:', prev.length);
        console.log('🔄 updateAnnotations callback - prev IDs:', prev.map(a => a.id));
        
        const updated = prev.map(a => {
          if (a.id === editingAnnotation.id) {
            console.log('✅ Found annotation to update:', a.id);
            return { ...a, text };
          }
          return a;
        });
        
        console.log('🔄 updateAnnotations callback - updated count:', updated.length);
        console.log('🔄 updateAnnotations callback - updated IDs:', updated.map(a => a.id));
        
        return updated;
      });
      setActionHistory(hist => [...hist, { type: 'edit', id: editingAnnotation.id, from: prevText, to: text }]);
    }
    setEditingAnnotation(null);
    setEditingText('');
    console.log('✅ Edit operation completed');
  };

  // Handler para cancelar edición
  const handleEditCancel = () => {
    setEditingAnnotation(null);
    setEditingText('');
  };

  // Handler para borrar anotación (soporta texto y highlight)
  const handleDeleteAnnotation = (annotation: Annotation) => {
    console.log('🗑️ SmartDocumentViewer - Deleting annotation:', annotation.id, annotation.type);
    console.log('📊 SmartDocumentViewer - Current annotations count before delete:', annotations.length);
    console.log('📊 SmartDocumentViewer - All annotations IDs:', annotations.map(a => a.id));
    
    // Usar directamente updateAnnotations para mantener la sincronización
    updateAnnotations(prev => {
      console.log('🔄 SmartDocumentViewer - updateAnnotations callback - prev count:', prev.length);
      console.log('🔄 SmartDocumentViewer - updateAnnotations callback - prev IDs:', prev.map(a => a.id));
      
      const filtered = prev.filter(a => a.id !== annotation.id);
      console.log('🔄 SmartDocumentViewer - updateAnnotations callback - filtered count:', filtered.length);
      console.log('🔄 SmartDocumentViewer - updateAnnotations callback - filtered IDs:', filtered.map(a => a.id));
      
      return filtered;
    });
    
    setActionHistory(hist => [...hist, { type: 'delete' as const, annotation }]);
    setEditingAnnotation(null);
    setEditingText('');
    
    console.log('✅ SmartDocumentViewer - Annotation delete operation completed');
  };

  // Navigation handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      console.log('📄 Going to page:', currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      console.log('📄 Going to page:', currentPage + 1);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPage = parseInt(event.target.value);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      console.log('📄 Going to page:', newPage);
    }
  };

  // Al iniciar el drag
  const handleStartDrag = (id: string, offset: { x: number; y: number }) => {
    setSelectedAnnotationId(id);
    setIsDragging(true);
    setDragOffset(offset);
    setDraggedAnnotationId(id);
    // Guardar la posición inicial
    const ann = annotations.find(a => a.id === id);
    if (ann) setDragStartPos({ x: ann.x, y: ann.y });
  };

  // Al finalizar el drag
  const handleEndDrag = (id: string, startX: number, startY: number, finalX: number, finalY: number) => {
    setIsDragging(false);
    setDragOffset(null);
    if (startX !== finalX || startY !== finalY) {
      console.log('Registrando acción de movimiento:', {
        id,
        from: { x: startX, y: startY },
        to: { x: finalX, y: finalY }
      });
      setActionHistory(hist => {
        const newHist = [...hist, { type: 'move' as const, id, from: { x: startX, y: startY }, to: { x: finalX, y: finalY } }];
        console.log('Historial actualizado:', newHist);
        return newHist;
      });
    } else {
      console.log('No hubo movimiento real, no se registra acción.');
    }
    setDraggedAnnotationId(null);
    setDragStartPos(null);
    setDragEndPos(null);
  };

  // Al mover una anotación:
  const handleMoveAnnotation = (id: string, newX: number, newY: number) => {
    updateAnnotations(prev => prev.map(a => {
      if (a.id === id) {
        if (a.type === 'freehand') {
          // Para freehand, necesitamos mover todos los puntos del trazo
          const deltaX = newX - a.x;
          const deltaY = newY - a.y;
          return {
            ...a,
            x: newX,
            y: newY,
            points: (a as any).points.map((p: any) => ({
              x: p.x + deltaX,
              y: p.y + deltaY
            }))
          };
        } else {
          // Para otras anotaciones, solo mover la posición base
          return { ...a, x: newX, y: newY };
        }
      }
      return a;
    }));
    if (id === draggedAnnotationId) {
      setDragEndPos({ x: newX, y: newY });
    }
  };

  // Handler para iniciar highlight
  const handleHighlightMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (currentTool !== 'highlight') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top + EXPORT_ICONS_HEIGHT) / zoom;
    setIsDrawingHighlight(true);
    setHighlightStart({ x, y });
    setHighlightRect({ x, y, width: 0, height: 0 });
  };

  // Handler para dibujar highlight
  const handleHighlightMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingHighlight || !highlightStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top + EXPORT_ICONS_HEIGHT) / zoom;
    setHighlightRect({
      x: Math.min(highlightStart.x, x),
      y: Math.min(highlightStart.y, y),
      width: Math.abs(x - highlightStart.x),
      height: Math.abs(y - highlightStart.y)
    });
  };

  // Handler para finalizar highlight
  const handleHighlightMouseUp = () => {
    if (!isDrawingHighlight || !highlightRect || highlightRect.width < 5 || highlightRect.height < 5) {
      setIsDrawingHighlight(false);
      setHighlightStart(null);
      setHighlightRect(null);
      return;
    }
    setIsDrawingHighlight(false);
    setHighlightStart(null);
    // Mostrar input para nota
    setHighlightNote('');
  };

  // Handler para guardar highlight
  const handleHighlightSubmit = (note: string) => {
    if (!highlightRect) return;
    const newHighlight: HighlightAnnotation = {
      id: generateUniqueId('highlight'),
      type: 'highlight',
      x: highlightRect.x,
      y: highlightRect.y,
      width: highlightRect.width,
      height: highlightRect.height,
      page: currentPage,
      style: currentStyle,
      createdAt: new Date(),
      createdBy: user?.username || 'Unknown',
      opacity: 0.6,
    };
    updateAnnotations(prev => [...prev, newHighlight]);
    setActionHistory(hist => [...hist, { type: 'add', annotation: newHighlight }]);
    setHighlightRect(null);
    setHighlightNote('');
  };

  // Handler para iniciar shape
  const handleShapeMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (currentTool !== 'shapes') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top + EXPORT_ICONS_HEIGHT) / zoom;
    setIsDrawingShape(true);
    setShapeStart({ x, y });
    setShapeRect({ x, y, width: 0, height: 0 });
  };

  // Handler para dibujar shape
  const handleShapeMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingShape || !shapeStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top + EXPORT_ICONS_HEIGHT) / zoom;
    setShapeRect({
      x: Math.min(shapeStart.x, x),
      y: Math.min(shapeStart.y, y),
      width: Math.abs(x - shapeStart.x),
      height: Math.abs(y - shapeStart.y)
    });
  };

  // Handler para finalizar shape
  const handleShapeMouseUp = () => {
    if (!isDrawingShape || !shapeRect || shapeRect.width < 5 || shapeRect.height < 5) {
      setIsDrawingShape(false);
      setShapeStart(null);
      setShapeRect(null);
      return;
    }
    setIsDrawingShape(false);
    const newShape: ShapeAnnotation = {
      id: generateUniqueId('shape'),
      type: 'shapes',
      x: shapeRect.x,
      y: shapeRect.y,
      width: shapeRect.width,
      height: shapeRect.height,
      page: currentPage,
      style: currentStyle,
      createdAt: new Date(),
      createdBy: user?.username || 'Unknown',
      shapeType: selectedShape,
    };
    updateAnnotations(prev => [...prev, newShape]);
    setActionHistory(hist => [...hist, { type: 'add', annotation: newShape }]);
    setShapeRect(null);
    // Abrir input de nota automáticamente
    setEditingAnnotation(newShape);
    setEditingText('');
  };

  // Handler para iniciar freehand
  const handleFreehandMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (currentTool !== 'freehand') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top + EXPORT_ICONS_HEIGHT) / zoom;
    setIsDrawingFreehand(true);
    setFreehandPoints([{ x, y }]);
  };

  // Handler para dibujar freehand
  const handleFreehandMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingFreehand) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top + EXPORT_ICONS_HEIGHT) / zoom;
    setFreehandPoints(prev => [...prev, { x, y }]);
  };

  // Handler para finalizar freehand
  const handleFreehandMouseUp = () => {
    if (!isDrawingFreehand || freehandPoints.length < 2) {
      setIsDrawingFreehand(false);
      setFreehandPoints([]);
      return;
    }
    setIsDrawingFreehand(false);
    
    // Calcular los límites del trazo
    const minX = Math.min(...freehandPoints.map(p => p.x));
    const maxX = Math.max(...freehandPoints.map(p => p.x));
    const minY = Math.min(...freehandPoints.map(p => p.y));
    const maxY = Math.max(...freehandPoints.map(p => p.y));
    
    // Crear anotación freehand
    const newFreehand: FreehandAnnotation = {
      id: generateUniqueId('freehand'),
      type: 'freehand',
      x: minX, // Usar el límite mínimo como posición base
      y: minY,
      page: currentPage,
      style: currentStyle,
      createdAt: new Date(),
      createdBy: user?.username || 'Unknown',
      points: freehandPoints,
      note: '',
    };
    
    updateAnnotations(prev => [...prev, newFreehand]);
    setActionHistory(hist => [...hist, { type: 'add', annotation: newFreehand }]);
    setFreehandPoints([]);
  };

  // Exportar PDF con anotaciones visuales
  const handleExportWithAnnotations = async () => {
    console.log('Starting PDF export with visual annotations...');
    if (!binaryData || !type.toLowerCase().includes('pdf')) {
      alert('Only PDF export is supported for now.');
      return;
    }

    // Initialize download progress modal
    setShowDownloadProgress(true);
    setDownloadProgress(0);
    setDownloadCurrentPage(0);
    setDownloadStatus('Initializing...');

    try {
      const pdfjsLib = await import('pdfjs-dist');
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
      const binaryDataCopy = binaryData.slice(0);
      const pdf = await pdfjsLib.getDocument({ data: binaryDataCopy }).promise;
      console.log('📄 PDF loaded with', pdf.numPages, 'pages');
      
      const totalPages = pdf.numPages;
      setDownloadTotalPages(totalPages);
      const pageImages: Array<{ dataUrl: string; width: number; height: number }> = [];
      // Usar un scale consistente para exportación (2.0 para mejor calidad)
      const scale = 2.0;
      const jpegQuality = 0.9;
      let totalImageBytes = 0;
      
      setDownloadStatus('Processing pages...');
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        try {
          console.log(`Processing page ${pageNum} of ${totalPages}`);
          setDownloadCurrentPage(pageNum);
          setDownloadProgress(Math.round((pageNum / totalPages) * 100));
          setDownloadStatus(`Processing page ${pageNum} of ${totalPages}...`);
          
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Could not get canvas context');
          await page.render({ canvasContext: ctx, viewport }).promise;
          const pageAnnotations = annotations.filter(a => a.page === pageNum);
          console.log(`Annotations on page ${pageNum}:`, pageAnnotations.length, pageAnnotations);
          drawAnnotationsOnCanvas(ctx, pageAnnotations, viewport, scale, EXPORT_ICONS_HEIGHT);
          const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
          const imageBytes = Math.round((dataUrl.length * 3) / 4 / 1024);
          totalImageBytes += imageBytes;
          console.log(`Page ${pageNum} image: ${imageBytes} KB`);
          pageImages.push({
            dataUrl,
            width: viewport.width,
            height: viewport.height
          });
        } catch (pageError) {
          console.error(`Error processing page ${pageNum}:`, pageError);
          setDownloadStatus(`Error on page ${pageNum}. Continuing...`);
          // Continue with next page instead of failing completely
          continue;
        }
      }
      
      console.log(`Total images: ${pageImages.length}, total approx: ${totalImageBytes} KB (${(totalImageBytes/1024).toFixed(2)} MB)`);
      setDownloadStatus('Generating final PDF...');
      
      if (pageImages.length === 0) {
        throw new Error('Could not process any pages of the PDF');
      }
      
      const pdfDoc = new jsPDF({ unit: 'px', format: [pageImages[0].width, pageImages[0].height] });
      pageImages.forEach((img, idx) => {
        if (idx > 0) pdfDoc.addPage([img.width, img.height], 'portrait');
        pdfDoc.addImage(img.dataUrl, 'JPEG', 0, 0, img.width, img.height);
      });
      
      setDownloadStatus('Downloading PDF...');
      const pdfBlob = pdfDoc.output('blob');
      console.log('Final PDF generated, size:', Math.round(pdfBlob.size/1024), 'KB');
      pdfDoc.save('document-with-annotations.pdf');
      
      // Close download progress modal
      setShowDownloadProgress(false);
      console.log('Export completed.');
    } catch (error) {
      console.error('Error exporting PDF with annotations:', error);
      setShowDownloadProgress(false);
      alert('Error exporting PDF with annotations: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  // Sobrescribe el prop para exportar con anotaciones visuales
  useEffect(() => {
    // Si el prop está presente, lo ignoramos y usamos la función visual
    (window as any).onDownloadWithAnnotations = handleExportWithAnnotations;
  }, [annotations, binaryData, type]);

  // Sobrescribe el prop para imprimir con anotaciones visuales
  useEffect(() => {
    // Si el prop está presente, lo ignoramos y usamos la función visual
    (window as any).onPrintWithAnnotations = handlePrintWithAnnotations;
  }, [annotations, binaryData, type]);

  // Sobrescribe el prop para imprimir sin anotaciones
  useEffect(() => {
    // Si el prop está presente, lo ignoramos y usamos la función local
    (window as any).onPrintWithoutAnnotations = handlePrintWithoutAnnotations;
  }, [binaryData, type]);

  // Función utilitaria para dibujar anotaciones visuales sobre un canvas
  // Las anotaciones están guardadas en coordenadas del documento (sin zoom)
  // Esta función las convierte a coordenadas del canvas de exportación/impresión
  function drawAnnotationsOnCanvas(ctx: CanvasRenderingContext2D, annotations: Annotation[], viewport: any, scale: number, offsetY: number = 0) {
    annotations.forEach(annotation => {
      // Convertir coordenadas del documento a coordenadas del canvas de exportación
      const exportX = annotation.x * scale;
      const exportY = annotation.y * scale - offsetY;
      const exportWidth = (annotation as any).width ? (annotation as any).width * scale : 0;
      const exportHeight = (annotation as any).height ? (annotation as any).height * scale : 0;
      
      if (annotation.type === 'highlight') {
        ctx.save();
        ctx.globalAlpha = annotation.opacity ?? 0.6;
        ctx.fillStyle = annotation.style.color;
        ctx.fillRect(exportX, exportY, exportWidth, exportHeight);
        ctx.restore();
      } else if (annotation.type === 'shapes') {
        ctx.save();
        ctx.strokeStyle = annotation.style.color;
        ctx.lineWidth = (annotation.style.thickness || 2);
        if (annotation.style.lineType === 'dashed') ctx.setLineDash([8, 4]);
        if (annotation.style.lineType === 'dotted') ctx.setLineDash([2, 4]);
        if (annotation.shapeType === 'rectangle') {
          ctx.strokeRect(exportX, exportY, exportWidth, exportHeight);
        } else if (annotation.shapeType === 'oval') {
          ctx.beginPath();
          ctx.ellipse(
            exportX + exportWidth / 2,
            exportY + exportHeight / 2,
            exportWidth / 2,
            exportHeight / 2,
            0, 0, 2 * Math.PI
          );
          ctx.stroke();
        }
        ctx.setLineDash([]);
        ctx.restore();
      } else if (annotation.type === 'text') {
        ctx.save();
        ctx.font = `${annotation.style.fontSize || 14}px ${annotation.style.fontFamily || 'Arial'}`;
        ctx.fillStyle = annotation.style.color;
        ctx.fillText(annotation.text, exportX, exportY + (annotation.style.fontSize || 14));
        ctx.restore();
      } else if (annotation.type === 'freehand') {
        ctx.save();
        ctx.strokeStyle = annotation.style.color;
        ctx.lineWidth = annotation.style.thickness || 2;
        ctx.beginPath();
        annotation.points.forEach((p, i) => {
          const pointX = p.x * scale;
          const pointY = p.y * scale - offsetY;
          if (i === 0) ctx.moveTo(pointX, pointY);
          else ctx.lineTo(pointX, pointY);
        });
        ctx.stroke();
        ctx.restore();
      }
    });
  }

  // Handlers para exportación
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchor(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportAnchor(null);
  };
  const handleExportWithoutAnnotations = async () => {
    if (!binaryData) return;
    const blob = new Blob([binaryData], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'document.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };
  const handlePrintWithAnnotations = async () => {
    console.log('🖨️ handlePrintWithAnnotations executing...');
    console.log('Starting PDF printing with visual annotations...');
    if (!binaryData || !type.toLowerCase().includes('pdf')) {
      alert('Only PDF printing is supported for now.');
      return;
    }

    // Initialize print progress modal
    setShowPrintProgress(true);
    setPrintProgress(0);
    setPrintCurrentPage(0);
    setPrintStatus('Initializing...');

    try {
      console.log('🖨️ Importing libraries...');
      setPrintStatus('Importing libraries...');
      const pdfjsLib = await import('pdfjs-dist');
      console.log('🖨️ pdfjs-dist imported');
      const jsPDFModule = await import('jspdf');
      console.log('🖨️ jspdf imported');
      const jsPDF = jsPDFModule.default;
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
      console.log('🖨️ Worker configured');
      const binaryDataCopy = binaryData.slice(0);
      console.log('🖨️ Binary data copy created');
      
      setPrintStatus('Loading PDF...');
      const pdf = await pdfjsLib.getDocument({ data: binaryDataCopy }).promise;
      console.log('🖨️ PDF loaded:', pdf.numPages, 'pages');
      
      const totalPages = pdf.numPages;
      setPrintTotalPages(totalPages);
      const pageImages: Array<{ dataUrl: string; width: number; height: number }> = [];
      // Usar un scale consistente para impresión (2.0 para mejor calidad)
      const scale = 2.0;
      const jpegQuality = 0.9;
      
      setPrintStatus('Processing pages...');
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        try {
          console.log(`Processing page ${pageNum} of ${totalPages} for printing`);
          setPrintCurrentPage(pageNum);
          setPrintProgress(Math.round((pageNum / totalPages) * 100));
          setPrintStatus(`Processing page ${pageNum} of ${totalPages}...`);
          
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Could not get canvas context');
          
          await page.render({ canvasContext: ctx, viewport }).promise;
          const pageAnnotations = annotations.filter(a => a.page === pageNum);
          console.log(`Annotations on page ${pageNum}:`, pageAnnotations.length, pageAnnotations);
          drawAnnotationsOnCanvas(ctx, pageAnnotations, viewport, scale, EXPORT_ICONS_HEIGHT);
          const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
          pageImages.push({
            dataUrl,
            width: viewport.width,
            height: viewport.height
          });
        } catch (pageError) {
          console.error(`Error processing page ${pageNum}:`, pageError);
          setPrintStatus(`Error on page ${pageNum}. Continuing...`);
          // Continue with next page instead of failing completely
          continue;
        }
      }
      
      console.log(`Total pages processed for printing: ${pageImages.length}`);
      setPrintStatus('Generating final PDF...');
      
      if (pageImages.length === 0) {
        throw new Error('Could not process any pages of the PDF');
      }
      
      const pdfDoc = new jsPDF({ unit: 'px', format: [pageImages[0].width, pageImages[0].height] });
      pageImages.forEach((img, idx) => {
        if (idx > 0) pdfDoc.addPage([img.width, img.height], 'portrait');
        pdfDoc.addImage(img.dataUrl, 'JPEG', 0, 0, img.width, img.height);
      });
      
      setPrintStatus('Opening PDF...');
      
      // Create blob and open in new window for printing
      const pdfBlob = pdfDoc.output('blob');
      console.log('🖨️ PDF blob created, size:', Math.round(pdfBlob.size/1024), 'KB');
      const url = window.URL.createObjectURL(pdfBlob);
      console.log('🖨️ URL created:', url);
      
      // Alternative method: create temporary link and open it
      try {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = 'document-with-annotations.pdf';
        
        // Add link to DOM temporarily
        document.body.appendChild(link);
        
        // Simulate click to open in new tab
        link.click();
        
        // Remove link from DOM
        document.body.removeChild(link);
        
        console.log('🖨️ PDF opened in new tab');
        
        // Close print progress modal
        setShowPrintProgress(false);
        
        // Show instructions to user
        alert('The PDF with annotations has been opened in a new tab. Please:\n\n1. Wait for the PDF to load completely\n2. Use Ctrl+P (or Cmd+P on Mac) to print\n3. Or use the browser\'s print button');
        
        // Clean up URL after a while
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          console.log('🖨️ URL released');
        }, 5000);
        
      } catch (error) {
        console.error('🖨️ Error opening PDF:', error);
        setShowPrintProgress(false);
        alert('Error opening the PDF. Try downloading it first and then printing manually.');
      }
      
      console.log('🖨️ Print process started successfully.');
    } catch (error) {
      console.error('🖨️ Error printing PDF with annotations:', error);
      setShowPrintProgress(false);
      alert('Error printing PDF with annotations: ' + (error instanceof Error ? error.message : String(error)));
    }
  };
  const handlePrintWithoutAnnotations = async () => {
    if (!binaryData) return;
    const blob = new Blob([binaryData], { type });
    const url = window.URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => win.print();
    }
  };

  if (!binaryData) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        border: '2px dashed #ddd',
        color: '#666'
      }}>
        No document loaded
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          height: 'calc(100vh - 64px)',
          overflow: 'auto',
          backgroundColor: '#f8f9fa',
          position: 'relative'
        }}
      >
        {/* Toolbar arriba, static */}
        <AnnotationToolbar
          onToolChange={handleToolChange}
          onStyleChange={handleStyleChange}
          onUndo={handleUndo}
          onSave={handleSave}
          currentTool={currentTool}
          currentStyle={currentStyle}
          canUndo={canUndo}
          selectedShape={selectedShape}
          onShapeChange={handleShapeChange}
          currentPage={currentPage}
          totalPages={totalPages}
          isPdf={type.includes('pdf')}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          handlePageChange={handlePageChange}
          onDownloadWithAnnotations={onDownloadWithAnnotations}
          onDownloadWithoutAnnotations={onDownloadWithoutAnnotations}
          onPrintWithAnnotations={onPrintWithAnnotations}
          onPrintWithoutAnnotations={onPrintWithoutAnnotations}
          annotationsVisible={annotationsVisible}
          onToggleAnnotationsVisibility={toggleAnnotationsVisibility}
          // Zoom controls
          zoom={zoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onZoomReset={handleZoomReset}
          onZoomChange={handleZoomChange}
          minZoom={minZoom}
          maxZoom={maxZoom}
          // Preview mode
          isPreview={previewInfo?.isPreview || false}
          isAdmin={isAdmin || false}
        />
        {/* Área centrada del PDF con scroll interno */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            minHeight: '100%',
          }}
        >
          {/* Contenedor relativo para canvas y overlays */}
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
            }}
            ref={containerRef}
            onWheel={handleWheelZoom}
          >
            {/* Justo antes del canvas del PDF: */}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2 }}>
              {previewInfo && previewInfo.isPreview ? (
                // Modo preview - mostrar mensaje en lugar de botones de descarga
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  backgroundColor: 'rgba(255, 193, 7, 0.1)', 
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  padding: '8px 12px'
                }}>
                  <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
                    {isAdmin ? 'Admin: Full download available' : 'Preview mode - Contact admin for full document'}
                  </Typography>
                  {isAdmin && (
                    <>
                      <Tooltip title="Download with annotations (Admin)">
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={handleExportWithAnnotations}
                          sx={{ ml: 1 }}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download without annotations (Admin)">
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={handleExportWithoutAnnotations}
                        >
                          <Download sx={{ opacity: 0.5 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              ) : (
                // Modo normal - mostrar botones de descarga
                <>
                  <Tooltip title="Download with annotations">
                    <IconButton color="primary" onClick={handleExportWithAnnotations}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download without annotations">
                    <IconButton color="primary" onClick={handleExportWithoutAnnotations}>
                      <Download sx={{ opacity: 0.5 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              
              {/* Botones de impresión siempre disponibles */}
              <Tooltip title="Print with annotations">
                <IconButton color="primary" onClick={() => {
                  console.log('🖨️ Icono de Print with annotations clickeado');
                  handlePrintWithAnnotations();
                }}>
                  <Print />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print without annotations">
                <IconButton color="primary" onClick={handlePrintWithoutAnnotations}>
                  <Print sx={{ opacity: 0.5 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              onMouseDown={
                currentTool === 'highlight' ? handleHighlightMouseDown : 
                currentTool === 'shapes' ? handleShapeMouseDown :
                currentTool === 'freehand' ? handleFreehandMouseDown : undefined
              }
              onMouseMove={
                currentTool === 'highlight' ? handleHighlightMouseMove : 
                currentTool === 'shapes' ? handleShapeMouseMove :
                currentTool === 'freehand' ? handleFreehandMouseMove : undefined
              }
              onMouseUp={
                currentTool === 'highlight' ? handleHighlightMouseUp : 
                currentTool === 'shapes' ? handleShapeMouseUp :
                currentTool === 'freehand' ? handleFreehandMouseUp : undefined
              }
              style={{
                display: 'block',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: '4px',
                background: 'white',
              }}
            />
            {isAddingText && textInputPosition && (
              <TextInputOverlay
                position={textInputPosition}
                onSubmit={handleTextSubmit}
                onCancel={handleTextCancel}
                style={{
                  color: currentStyle.color,
                  fontSize: currentStyle.fontSize,
                  fontFamily: currentStyle.fontFamily,
                }}
              />
            )}
            {editingAnnotation && (
              <TextInputOverlay
                position={{ x: editingAnnotation.x, y: (editingAnnotation as any).y + ((editingAnnotation as any).height || 0) + 10 }}
                onSubmit={handleEditSubmit}
                onCancel={handleEditCancel}
                style={{ color: '#000', fontSize: 14, fontFamily: 'Arial', backgroundColor: '#fff' }}
                initialValue={editingText}
                onDelete={() => handleDeleteAnnotation(editingAnnotation)}
              />
            )}
            {highlightRect && !isDrawingHighlight && (
              <TextInputOverlay
                position={{ x: highlightRect.x, y: highlightRect.y + highlightRect.height + 10 }}
                onSubmit={handleHighlightSubmit}
                onCancel={() => { setHighlightRect(null); setHighlightNote(''); }}
                style={{ color: currentStyle.color, fontSize: 14, fontFamily: 'Arial' }}
                initialValue={highlightNote}
              />
            )}
            {/* Renderizar la shape mientras se dibuja */}
            {isDrawingShape && shapeRect && (
              <div
                style={{
                  position: 'absolute',
                  left: shapeRect.x * zoom,
                  top: shapeRect.y * zoom,
                  width: shapeRect.width * zoom,
                  height: shapeRect.height * zoom,
                  border: `2px dashed ${currentStyle.color}`,
                  borderRadius: selectedShape === 'oval' ? '50%' : '4px',
                  pointerEvents: 'none',
                  zIndex: 1000,
                }}
              />
            )}
            {/* Renderizar el rectángulo highlight mientras se dibuja */}
            {isDrawingHighlight && highlightRect && (
              <div
                style={{
                  position: 'absolute',
                  left: highlightRect.x * zoom,
                  top: highlightRect.y * zoom,
                  width: highlightRect.width * zoom,
                  height: highlightRect.height * zoom,
                  background: `${currentStyle.color}99`, // 60% transparencia
                  border: `1px dashed ${currentStyle.color}`,
                  pointerEvents: 'none',
                  zIndex: 1000,
                }}
              />
            )}
            {/* Renderizar freehand mientras se dibuja */}
            {isDrawingFreehand && freehandPoints.length > 1 && (
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 1000,
                }}
              >
                <path
                  d={`M ${freehandPoints.map(p => `${p.x * zoom} ${p.y * zoom}`).join(' L ')}`}
                  stroke={currentStyle.color}
                  strokeWidth={currentStyle.thickness}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {annotationsVisible && (
              <AnnotationRenderer
                annotations={annotations.filter(ann => {
                  // Filter by page and ensure annotation has valid coordinates
                  const isValidPage = ann.page === currentPage || ann.page === undefined;
                  const hasValidCoords = typeof ann.x === 'number' && typeof ann.y === 'number';
                  return isValidPage && hasValidCoords;
                })}
                scale={1}
                zoom={zoom}
                currentTool={currentTool}
                onSelectAnnotation={id => setSelectedAnnotationId(id)}
                selectedAnnotationId={selectedAnnotationId}
                onMoveAnnotation={handleMoveAnnotation}
                onStartDrag={handleStartDrag}
                onEndDrag={handleEndDrag}
                onEditAnnotation={handleEditAnnotation}
                onDeleteAnnotation={handleDeleteAnnotation}
                isDrawingHighlight={isDrawingHighlight}
                isDrawingShape={isDrawingShape}
                isDrawingFreehand={isDrawingFreehand}
              />
            )}

          </div>
        </div>
        {/* Reemplazo el menú de exportación por 4 iconos alineados a la derecha: */}
        {/* <Box sx={{ position: 'absolute', top: 8, right: 24, zIndex: 100, display: 'flex', alignItems: 'center', gap: 1, mt: 7 }}> */}
        {/*   <Tooltip title="Download with annotations"> */}
        {/*     <IconButton color="primary" onClick={handleExportWithAnnotations}> */}
        {/*       <Download /> */}
        {/*     </IconButton> */}
        {/*   </Tooltip> */}
        {/*   <Tooltip title="Download without annotations"> */}
        {/*     <IconButton color="primary" onClick={handleExportWithoutAnnotations}> */}
        {/*       <Download sx={{ opacity: 0.5 }} /> */}
        {/*     </IconButton> */}
        {/*   </Tooltip> */}
        {/*   <Tooltip title="Print with annotations"> */}
        {/*     <IconButton color="primary" onClick={handlePrintWithAnnotations}> */}
        {/*       <Print /> */}
        {/*     </IconButton> */}
        {/*   </Tooltip> */}
        {/*   <Tooltip title="Print without annotations"> */}
        {/*     <IconButton color="primary" onClick={handlePrintWithoutAnnotations}> */}
        {/*       <Print sx={{ opacity: 0.5 }} /> */}
        {/*     </IconButton> */}
        {/*   </Tooltip> */}
        {/* </Box> */}
        
        {/* Print Progress Modal */}
        {showPrintProgress && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 3,
                padding: 4,
                maxWidth: 500,
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                🖨️ Processing PDF for Printing
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
                {printStatus}
              </Typography>
              
              {printTotalPages > 0 && (
                <Typography variant="body2" sx={{ mb: 2, color: '#888' }}>
                  Page {printCurrentPage} of {printTotalPages} ({printProgress}%)
                </Typography>
              )}
              
              <Box sx={{ width: '100%', mb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 20,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 10,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: `${printProgress}%`,
                      height: '100%',
                      backgroundColor: '#1976d2',
                      borderRadius: 10,
                      transition: 'width 0.3s ease',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                        animation: 'shimmer 2s infinite',
                      },
                    }}
                  />
                </Box>
              </Box>
              
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Please wait, this may take several minutes for large PDFs...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Download Progress Modal */}
        {showDownloadProgress && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 3,
                padding: 4,
                maxWidth: 500,
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                ⬇️ Downloading PDF with Annotations
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
                {downloadStatus}
              </Typography>
              
              {downloadTotalPages > 0 && (
                <Typography variant="body2" sx={{ mb: 2, color: '#888' }}>
                  Page {downloadCurrentPage} of {downloadTotalPages} ({downloadProgress}%)
                </Typography>
              )}
              
              <Box sx={{ width: '100%', mb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 20,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 10,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: `${downloadProgress}%`,
                      height: '100%',
                      backgroundColor: '#1976d2',
                      borderRadius: 10,
                      transition: 'width 0.3s ease',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                        animation: 'shimmer 2s infinite',
                      },
                    }}
                  />
                </Box>
              </Box>
              
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                Please wait, this may take several minutes for large PDFs...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Preview Mode Alert */}
        {previewInfo && previewInfo.isPreview && (
          <Box
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              maxWidth: 400,
              zIndex: 1000,
            }}
          >
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid #ffc107',
                '& .MuiAlert-icon': {
                  color: '#ff9800',
                },
                '& .MuiAlert-message': {
                  color: '#e65100',
                }
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                  📄 Preview Mode
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#e65100' }}>
                {previewInfo.previewMessage}
              </Typography>
              <Typography variant="body2" sx={{ color: '#e65100', fontSize: '0.875rem' }}>
                {isAdmin ? (
                  <>
                    <AdminIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Admin access: You can download the full document.
                  </>
                ) : (
                  <>
                    <WarningIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Contact an administrator to download the full document.
                  </>
                )}
              </Typography>
            </Alert>
          </Box>
        )}
      </div>
      
      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
    </>
  );
};

export default SmartDocumentViewer; 